import { Card } from "@/components/ui/card";
import type { CLOCK_CADENCE } from "@/providers/wall-clock";
import type { DashboardSpec } from "./ChartSpec";
import { DataViewerProvider } from "./data-viewer-provider";
import { PanelStack } from "./panels/panel-stack";
import { PanelLayoutToggle } from "./spec/panel-layout-toggle";
import { SpecProvider } from "./spec/spec-context";
import { AutoRefreshTicker } from "./time-window/auto-refresh-ticker";
import { LastUpdatedBar } from "./time-window/last-updated-bar";
import { WindowRangePicker } from "./time-window/window-range-picker";

function DataViewerMessage({ message }: { message: string }) {
    return (
        <Card>
            <div className={"text-muted-foreground text-sm text-center"}>{message}</div>
        </Card>
    );
}

export function DataViewer({
    initialSpec,
    isLoading,
    isError,
    isEmpty,
    autoRefreshCadence,
    resizable = true,
}: {
    initialSpec: DashboardSpec;
    isLoading?: boolean;
    isError?: boolean;
    isEmpty?: boolean;
    autoRefreshCadence?: CLOCK_CADENCE;
    /** When false, panels fill available width at default height with no drag grips. */
    resizable?: boolean;
}) {
    if (isLoading) {
        return <DataViewerMessage message={"Loading data streams..."} />;
    }
    if (isError) {
        return <DataViewerMessage message={"Failed to load data streams."} />;
    }
    if (isEmpty) {
        return <DataViewerMessage message={"No data streams for this zone."} />;
    }

    return (
        <SpecProvider initialSpec={initialSpec}>
            <DataViewerProvider defaultTimeRange={initialSpec.defaultTimeRange}>
                <div className={"flex mb-3 gap-2 items-center justify-between"}>
                    <div className={"flex items-center gap-2"}>
                        <PanelLayoutToggle />
                        <WindowRangePicker />
                    </div>
                    {autoRefreshCadence != null && (
                        <>
                            <AutoRefreshTicker cadence={autoRefreshCadence} />
                            <LastUpdatedBar />
                        </>
                    )}
                </div>
                <PanelStack resizable={resizable} />
            </DataViewerProvider>
        </SpecProvider>
    );
}
